import { useState } from "react";
import { History, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AdminTitle } from "@/admin/AdminLayout";
import { useApi } from "@/lib/api";
import { fmtDateTime, ROLE_LABEL, rp } from "@/lib/format";

const FIELD_LABEL = {
  name: "Nama", email: "Email", phone: "No. HP", nik: "NIK", address: "Alamat", status: "Status", role: "Peran",
  type: "Jenis", direction: "Arah", amount: "Nominal", description: "Keterangan", pin: "PIN", decision: "Keputusan",
  note: "Catatan", admin_note: "Catatan", approve: "Keputusan", user_id: null,
};
const fmtVal = (k, v) => {
  if (k === "amount" && typeof v === "number") return rp(v);
  if (k === "direction") return v === "in" ? "Setoran" : "Penarikan";
  if (k === "role") return ROLE_LABEL[v] || v;
  if (k === "approve") return v ? "Disetujui" : "Ditolak";
  if (k === "type" || k === "status") return String(v ?? "-").replace(/^./, (c) => c.toUpperCase());
  return String(v ?? "-");
};
const isEmpty = (v) => v === null || v === undefined || v === "";

const ACTION_TONE = (a) =>
  /Hapus|Tolak/.test(a) ? "bg-rose-100 text-rose-700" : /PIN/.test(a) ? "bg-violet-100 text-violet-700"
    : /Edit|Ubah/.test(a) ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

const Detail = ({ log }) => {
  if (log.changes && Object.keys(log.changes).length) {
    return Object.entries(log.changes).map(([k, c]) => (
      <div key={k}><b>{FIELD_LABEL[k] || k}:</b> <span className="line-through text-slate-400">{fmtVal(k, c.dari)}</span> → <span className="text-slate-900">{fmtVal(k, c.menjadi)}</span></div>
    ));
  }
  const rows = Object.entries(log.detail || {}).filter(([k, v]) => FIELD_LABEL[k] !== null && !isEmpty(v));
  if (!rows.length) return <span className="text-slate-400">{log.target_email || "-"}</span>;
  return rows.map(([k, v]) => (
    <div key={k}><b>{FIELD_LABEL[k] || k}:</b> {fmtVal(k, v)}</div>
  ));
};

export default function AdminLogs() {
  const { data } = useApi("/admin/logs");
  const [q, setQ] = useState("");
  const list = (data || []).filter((l) => `${l.action} ${l.actor_name} ${l.target_name} ${l.target_member_no} ${l.target_email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <AdminTitle icon={History} title="Log Perubahan" sub="Semua perubahan data yang disetujui dengan PIN Super Admin" />
      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
        <Input data-testid="logs-search" className="pl-9 bg-white h-10" placeholder="Cari aksi, pelaku, atau anggota" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="logs-table">
          <thead className="bg-gradient-to-r from-slate-50 to-blue-50 text-left text-xs font-mono uppercase tracking-wide text-slate-600">
            <tr><th className="p-3">Waktu</th><th className="p-3">Aksi</th><th className="p-3">Dilakukan oleh</th><th className="p-3">Anggota / Target</th><th className="p-3">Rincian</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500" data-testid="logs-empty">Belum ada log perubahan.</td></tr>}
            {list.map((l) => (
              <tr key={l.log_id} className="border-t border-slate-100 align-top hover:bg-blue-50/40" data-testid={`log-${l.log_id}`}>
                <td className="p-3 whitespace-nowrap text-xs text-slate-600">{fmtDateTime(l.created_at)}</td>
                <td className="p-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${ACTION_TONE(l.action)}`}>{l.action}</span></td>
                <td className="p-3"><div className="font-semibold">{l.actor_name}</div><div className="text-xs text-slate-500">{ROLE_LABEL[l.actor_role]}</div></td>
                <td className="p-3"><div className="font-semibold">{l.target_name || "-"}</div><div className="text-xs text-slate-500">{l.target_member_no || l.target_email || ""}</div></td>
                <td className="p-3 text-xs text-slate-700 space-y-0.5 min-w-[220px]"><Detail log={l} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
